/**
 * UI kit tokens — the light "card" language, in one place.
 *
 * The in-game HUD is dark glass; this is the other half of the app: the surfaces the player
 * reads while stopped (explore panel today, world dialog and build menu next). Every value that
 * would otherwise be re-typed per panel lives here, so a change to the radius, the shadow or the
 * accent lands everywhere at once.
 *
 * Touch first: controls are 40 px tall on touch and tighten a step on desktop, which is why the
 * height token is a pair of classes rather than a number.
 */

/** The wordmark's two greens (its gradient stops) — the only accent colours the kit uses. */
export const GREEN = '#93CB55';
export const GREEN_DEEP = '#025C3B';

/**
 * The logo mark's own gradient, sampled off the rounded-square glyph: dark green at the bottom
 * running to pale green at the top, leaning ~18° right of vertical (CSS 0deg = to top).
 * A background-image string rather than a class — Tailwind has no arbitrary gradient-angle utility
 * that reads as clearly as the sampled values do here.
 */
export const MARK_GRADIENT_FROM = '#005831';
export const MARK_GRADIENT_TO = '#C9ED95';
export const MARK_GRADIENT_CSS =
  `linear-gradient(18deg, ${MARK_GRADIENT_FROM} 0%, ${MARK_GRADIENT_TO} 100%)`;

/** The card's lift: tight, dark, and cast down-right — one light source for every surface.
 *  Shared so floating controls sit at the same height as the panels. */
export const PANEL_SHADOW = 'shadow-[2px_2px_5px_rgba(0,0,0,0.55)]';
/** Same light, shorter throw — a popover sits closer to the surface it opens from. */
export const POPOVER_SHADOW = 'shadow-[2px_2px_5px_rgba(0,0,0,0.35)]';

/** The card itself: white, softly rounded, lifted off the world with the logo's drop-shadow. */
// Uniform padding, deliberately: the wordmark sits at the top-left of the card, so its left inset
// and its top inset are the same measurement and have to stay equal.
export const PANEL_SURFACE = `bg-white rounded-3xl ${PANEL_SHADOW} p-3 md:p-3.5`;

/** Vertical rhythm between rows inside a panel. */
export const PANEL_STACK = 'flex flex-col gap-2 md:gap-1.5';

/** Row label — quiet, in a fixed column to the left of its control so rows line up. */
export const ROW_LABEL = 'text-[10px] font-semibold uppercase tracking-wide text-neutral-400 truncate';

/** Width of that column, for the label-left rows (ExplorePanel's). */
export const ROW_LABEL_W = 'w-[68px]';

/**
 * THE GENERATED FORM'S OWN COLUMNS, and they are fixed so that everything lines up down the sheet.
 *
 * A slider says nothing without its ends — a handle two thirds along is two thirds of WHAT — so the
 * range sits at both ends of the track. That only reads if the columns are fixed: sized to their
 * content, a `0…50` row and a `50…4000` row start their tracks in different places, every slider is
 * a different length, and the value boxes wander down the right-hand side.
 *
 * The title is the only part that shrinks, and it wraps rather than truncating: "Partition
 * thickness" does not fit 92 px on one line at any size worth reading.
 */
export const PARAM_TITLE_W = 'w-[92px] min-w-[56px]';
export const PARAM_BOUND_W = 'w-[34px]';
export const PARAM_UNIT_W = 'w-[20px]';
export const PARAM_VALUE_W = 'w-[52px]';

/** Value readout under a row label (the time, a percentage). */
export const ROW_VALUE = 'text-[10px] font-semibold tabular-nums text-neutral-400';

/** Interactive height for every control in a panel. */
export const CONTROL_H = 'h-10 md:h-9';

/** Recessed track shared by the segmented toggle and the dropdown trigger. */
export const TRACK = 'rounded-full bg-neutral-100 ring-1 ring-inset ring-black/5';

/** Accent fill (the sliding pill, the slider) and the text/icon colour that reads on it. */
export const ACCENT_BG = 'bg-[#93CB55]';
export const ACCENT_ON = 'text-[#025C3B]';
/** Accent for glyphs on a WHITE surface — the light green is too faint there. */
export const ACCENT_TEXT = 'text-[#025C3B]';

/** Segment labels off the pill, and while the control is locked. */
export const SEGMENT_OFF = 'text-neutral-500';
export const SEGMENT_DISABLED = 'text-neutral-300';

/** One transition for everything that slides or fades in a panel. */
export const MOTION = 'transition-all duration-200 ease-out';

/** Popover (dropdown list) surface — a smaller sibling of the panel. */
export const POPOVER_SURFACE = `rounded-2xl bg-white ${POPOVER_SHADOW} ring-1 ring-black/5 p-1.5`;

/**
 * Floating round button in the card's language — the mark's gradient behind white glyphs, ringed by
 * a white border the same 3 px as the marker stem so button and stem read as one drawn object.
 * The fill itself is a background-image (MARK_GRADIENT_CSS), applied by the caller.
 */
export const BUTTON_SURFACE = `border-[3px] border-white text-white ${PANEL_SHADOW}`;

